import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Button } from 'components/common';
import {
  submitPaymentRequestBankInstruction, refreshPaymentRequestBankStatus, getPaymentRequest,
  resolvePaymentRequestBankUncertainty, retryPaymentRequestRelease, reopenPaymentRequestBankResolution,
  markPaymentRequestDisbursementPaid,
} from "redux/erp/paymentRequestSlice";
import DisbursementPanel from "./DisbursementPanel";
import UnderlineInput from "../common/UnderlineInput";
import Clickable from "../common/Clickable";
import HsbcStatusPanel from "./HsbcStatusPanel";

// Trạng thái tiền tách khỏi trạng thái duyệt: duyệt xong KHÔNG phải là đã chi.
const BANK_STATUS = {
  not_sent:          { label: "Not sent", tone: "tw-bg-geist-100 tw-text-geist-fg-2" },
  pending_approvals: { label: "Waiting for approvals", tone: "tw-bg-[#fff8ee] tw-text-[#a05e03]" },
  processing:        { label: "Processing at bank", tone: "tw-bg-[#eef4ff] tw-text-geist-accent" },
  paid:              { label: "Paid", tone: "tw-bg-[#edfaef] tw-text-geist-success" },
  failed:            { label: "Failed", tone: "tw-bg-[#fff0f0] tw-text-geist-error" },
};

const OP_TONE = {
  succeeded:   "tw-text-geist-success",
  rejected:    "tw-text-geist-error",
  unreachable: "tw-text-[#a05e03]",
  not_started: "tw-text-geist-fg-3",
};

const Pill = ({ children, tone }) => (
  <span className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-1 tw-rounded tw-text-[12px] tw-font-bold ${tone}`}>
    {children}
  </span>
);

Pill.propTypes = {
  children: PropTypes.node,
  tone: PropTypes.string,
};

/**
 * ERP không còn adapter mock nào — dữ liệu PO, tài khoản và bút toán đều lấy từ API
 * thật. Nên nhãn ở cột ERP không còn nghĩa "đang chạy mock" mà là "chưa cấu hình
 * được API" (thiếu ERP_API_URL). Ngân hàng thì vẫn còn adapter mock, giữ MockTag.
 */
const NotConfiguredTag = () => (
  <span title="ERP API not configured — go to Settings → Integrations → ERP"
    className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-bold tw-uppercase tw-bg-[#fff8ee] tw-text-[#a05e03] tw-border tw-border-solid tw-border-[#f7e2c0]">
    <i className="bi bi-plug" /> Not configured
  </span>
);

const MockTag = () => (
  <span title="Running the mock adapter — not connected to the real API"
    className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-bold tw-uppercase tw-bg-[#fff8ee] tw-text-[#a05e03] tw-border tw-border-solid tw-border-[#f7e2c0]">
    <i className="bi bi-cone-striped" /> Mock
  </span>
);

/**
 * ERP và ngân hàng — hai chiều trạng thái tách rời nhau và tách khỏi trạng thái duyệt.
 *
 * Card này cố tình dán nhãn "Mock" khi adapter tương ứng chưa phải API thật, để
 * demo mock không bị hiểu nhầm là đã tích hợp xong (điều kế hoạch nhắc rõ).
 */
const IntegrationsCard = ({ paymentRequest: pr }) => {
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [hsbcRef, setHsbcRef] = useState("");
  const [resolveReason, setResolveReason] = useState("");
  // "not_received" clears the request for a resend; "received" records HSBC's reference.
  const [resolveOutcome, setResolveOutcome] = useState("not_received");
  const [reopening, setReopening] = useState(false);
  const [reopenReason, setReopenReason] = useState("");

  const bank = pr?.bank || {};
  const erp = pr?.erp_operations || {};
  const docs = pr?.documents_state || {};
  const perms = pr?.permissions || {};
  const sequence = erp.sequence || ["create"];

  // Lời của server thắng lời soạn sẵn: HSBC từ chối hay không trả lời thì server vẫn
  // trả 200 kèm `warning`, và trước đây màn hình vẫn báo "Bank instruction sent.".
  // A second click before React re-renders the disabled button must not send twice.
  const busyRef = useRef(false);
  const run = async (thunk, okMessage) => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(true);
    try {
      const res = await dispatch(thunk).unwrap();
      await dispatch(getPaymentRequest(pr.id));
      if (res?.warning) {
        toast.warning(res.message, { autoClose: 12000 });
        return false;
      }
      toast.success(res?.message || okMessage);
      return true;
    } catch (e) {
      toast.error(e?.error || e?.message || "Action failed.");
      return false;
    } finally { busyRef.current = false; setBusy(false); }
  };

  // "Change bank" / "Request documents" đã bỏ khỏi card (2026-10-26): ngân hàng đi
  // theo tài khoản đối ứng của bút toán APP (PaymentRoute), chứng từ bổ sung không còn
  // dùng. Trạng thái "Supplement requested" cũ vẫn hiển thị bên dưới.

  const [retrying, setRetrying] = useState(false);
  const [retryReason, setRetryReason] = useState("");
  const saveRetry = async () => {
    const ok = await run(retryPaymentRequestRelease({ id: pr.id, reason: retryReason.trim() }), "Payment queued to be sent to HSBC again.");
    if (ok) { setRetrying(false); setRetryReason(""); }
  };

  const saveReopen = async () => {
    const ok = await run(reopenPaymentRequestBankResolution({ id: pr.id, reason: reopenReason.trim() }),
      "Recorded HSBC result undone — check HSBCnet and record it again.");
    if (ok) { setReopening(false); setReopenReason(""); }
  };

  const saveResolve = async () => {
    const received = resolveOutcome === "received";
    if (received && !hsbcRef.trim()) { toast.warning("Enter the reference HSBCnet shows for this payment."); return; }
    if (!resolveReason.trim()) { toast.warning("Say what HSBC reported."); return; }
    const ok = await run(
      resolvePaymentRequestBankUncertainty({
        id: pr.id, reference: received ? hsbcRef.trim() : "", reason: resolveReason.trim(),
      }),
      received ? "Recorded — the bank reference is now tracked."
               : "Recorded — you can send the payment again."
    );
    if (ok) { setResolving(false); setHsbcRef(""); setResolveReason(""); }
  };

  const status = BANK_STATUS[bank.status] || BANK_STATUS.not_sent;
  const unknown = !!bank.outcome_unknown;
  const reversal = pr?.journal_reversal || {};
  // The release stays "unknown" until it is sent again; once HSBCnet was checked, say so
  // instead of repeating the stale state.
  // Payment Accountant handles this payment after BOD (Trade loan / Non-HSBC / option on).
  const disbursement = pr?.disbursement || {};
  const byPaymentAccountant = ["expected", "processing", "paid"].includes(disbursement.status);
  const markPaid = ({ reference, note }) => run(
    markPaymentRequestDisbursementPaid({ id: pr.id, reference, note }), "Payment recorded as paid."
  );
  const releaseChecked = bank.automatic_release?.state === "unknown" && !unknown && !!bank.resolved_by;
  const releaseStatus = !releaseChecked ? bank.automatic_release?.state
    : bank.manual_reference ? `checked on HSBCnet by ${bank.resolved_by} — HSBC received it`
      : `checked on HSBCnet by ${bank.resolved_by} — not received, ready to send again`;

  return (
    <div id="payment-request-bank-card" className="tw-scroll-mt-4 tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-overflow-hidden">
      <div className="tw-px-5 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border tw-flex tw-items-center tw-gap-2">
        <i className="bi bi-plug tw-text-geist-fg-3" />
        <span className="tw-text-sm tw-font-semibold tw-text-geist-fg">ERP &amp; bank</span>
      </div>

      <div className="tw-p-5 tw-space-y-5">
        {/* ERP — trạng thái từng thao tác, không gộp */}
        <div>
          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
            <span className="tw-text-[12px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-geist-fg-3">ERP journal</span>
            {!erp.live && <NotConfiguredTag />}
          </div>
          <div className="tw-space-y-1.5">
            {sequence.map((op) => {
              const o = erp[op] || {};
              const st = o.status || "not_started";
              return (
                <div key={op} className="tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-text-sm">
                  <span className="tw-text-geist-fg-2 tw-capitalize">{op}</span>
                  <span className="tw-flex-1 tw-border-0 tw-border-b tw-border-dotted tw-border-geist-border" />
                  <span className={`tw-font-semibold ${OP_TONE[st] || OP_TONE.not_started}`}>{st.replace("_", " ")}</span>
                  {o.reference && <span className="tw-text-[12px] tw-text-geist-fg-3 tw-tabular-nums">{o.reference}</span>}
                  {o.attempts > 1 && <span className="tw-text-[12px] tw-text-geist-fg-3">×{o.attempts}</span>}
                </div>
              );
            })}
          </div>
          {sequence.some((op) => erp[op]?.last_error) && (
            <p className="tw-text-[12px] tw-text-geist-error tw-m-0 tw-mt-2">
              {sequence.map((op) => erp[op]?.last_error).filter(Boolean)[0]}
            </p>
          )}
        </div>

        {/* Ngân hàng */}
        <div className="tw-pt-4 tw-border-0 tw-border-t tw-border-solid tw-border-geist-border">
          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2 tw-flex-wrap">
            <span className="tw-text-[12px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-geist-fg-3">Bank</span>
            {!bank.live && <MockTag />}
            <span className="tw-flex-1" />
            <Pill tone={status.tone}>{status.label}</Pill>
          </div>

          <>
              <div className="tw-text-sm tw-text-geist-fg">
                {bank.route ? (bank.route === "hsbc" ? "HSBC" : "Non-HSBC") : "Not decided yet"}
                {bank.reference && <span className="tw-text-geist-fg-3 tw-text-[13px]"> · {bank.reference}</span>}
              </div>
              {bank.changed_by && (
                <div className="tw-text-[12px] tw-text-geist-fg-3 tw-mt-1">
                  Changed by {bank.changed_by}{bank.changed_at ? ` · ${bank.changed_at}` : ""}
                  {bank.reason && <div className="tw-italic tw-mt-0.5">“{bank.reason}”</div>}
                </div>
              )}
              {bank.manual_reference && (
                <div className="tw-text-[12px] tw-text-geist-fg-3 tw-mt-1">
                  Reference recorded by hand by {bank.resolved_by || "—"}
                  {bank.resolved_reason && <> · “{bank.resolved_reason}”</>}
                  {perms.allow_reopen_bank_resolution && !reopening && (
                    <Clickable onClick={() => setReopening(true)} className="tw-ml-2 tw-font-semibold tw-text-geist-accent">
                      Wrong? Undo
                    </Clickable>
                  )}
                </div>
              )}
              {reopening && (
                <div className="tw-mt-2 tw-space-y-2">
                  <UnderlineInput aria-label="Why the recorded HSBC result is wrong" value={reopenReason}
                    onChange={(e) => setReopenReason(e.target.value)}
                    placeholder="Why it is wrong, e.g. a note was typed into the reference field" />
                  <div className="tw-flex tw-gap-2">
                    <Button size="sm" loading={busy} disabled={!reopenReason.trim()} onClick={saveReopen}>Undo recorded result</Button>
                    <Clickable onClick={() => { setReopening(false); setReopenReason(""); }}
                      className="tw-h-8 tw-px-3 tw-text-[12px] tw-text-geist-fg-3">Cancel</Clickable>
                  </div>
                </div>
              )}
              <p className="tw-text-[12px] tw-text-geist-fg-3 tw-m-0 tw-mt-2">
                Approval and payment are tracked separately — an approved request has not been paid yet.
              </p>
            </>
        </div>

        {byPaymentAccountant && (
          <DisbursementPanel disbursement={disbursement} hsbc={bank.route === "hsbc"} busy={busy}
            canMarkPaid={!!perms.allow_mark_disbursement_paid} onMarkPaid={markPaid} />
        )}

        {/* Hồ sơ chứng từ */}
        {docs.status === "supplement_requested" && (
          <div className="tw-pt-4 tw-border-0 tw-border-t tw-border-solid tw-border-geist-border">
            <Pill tone="tw-bg-[#fff8ee] tw-text-[#a05e03]">Supplement requested</Pill>
            <p className="tw-text-[13px] tw-text-geist-fg-2 tw-m-0 tw-mt-2">{docs.note}</p>
            {docs.requested_by && (
              <p className="tw-text-[12px] tw-text-geist-fg-3 tw-m-0 tw-mt-1">
                {docs.requested_by}{docs.requested_at ? ` · ${docs.requested_at}` : ""}
              </p>
            )}
          </div>
        )}


        {/* Chưa rõ HSBC đã nhận lệnh chưa. KHÔNG phải "thất bại": lệnh có thể đã vào
            hàng đợi của HSBC. Gửi lại mù ở đây là chi hai lần, nên chặn nút gửi và
            bắt người dùng tra cứu rồi khai kết quả. */}
        {unknown && (
          <div className="tw-rounded-geist-sm tw-border tw-border-solid tw-border-[#f7e2c0] tw-bg-[#fff8ee] tw-p-3.5">
            <div className="tw-flex tw-items-start tw-gap-2">
              <i className="bi bi-exclamation-triangle-fill tw-text-[#a05e03] tw-mt-0.5" />
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-text-[13px] tw-font-bold tw-text-[#7a4a02]">
                  Unclear whether HSBC received this payment
                </div>
                <p className="tw-text-[12px] tw-text-[#7a4a02] tw-m-0 tw-mt-1">
                  {bank.last_error || "The bank did not give a definite answer."} Check on HSBCnet
                  before sending again — resending a payment that is already queued pays twice.
                </p>
                {perms.bank_action_own_request && (
                  <p className="tw-text-[12px] tw-font-semibold tw-text-[#7a4a02] tw-m-0 tw-mt-2">
                    You created this request, so another accountant or admin must check HSBCnet, record the
                    result and send the payment again.
                  </p>
                )}
                {!resolving && perms.allow_resolve_bank_uncertainty && (
                  <Clickable onClick={() => setResolving(true)}
                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-2.5 tw-mt-2.5 tw-rounded-geist-sm tw-border tw-border-solid tw-border-[#e0c89a] tw-bg-geist-bg tw-text-[12px] tw-font-semibold tw-text-[#7a4a02]">
                    <i className="bi bi-check2-square" /> Record what HSBCnet shows
                  </Clickable>
                )}
                {resolving && (
                  <div className="tw-mt-3 tw-space-y-2.5">
                    <div role="radiogroup" aria-label="What HSBCnet shows" className="tw-space-y-1.5">
                      {[
                        ["not_received", "HSBC did NOT receive it — clear it to send again"],
                        ["received", "HSBC received it — record its reference"],
                      ].map(([value, label]) => (
                        <label key={value} className="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-text-[#7a4a02] tw-cursor-pointer">
                          <input type="radio" name="hsbc-outcome" value={value} checked={resolveOutcome === value}
                            onChange={() => setResolveOutcome(value)} />
                          {label}
                        </label>
                      ))}
                    </div>
                    {resolveOutcome === "received" && (
                      <div>
                        <label className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-[#7a4a02]">
                          HSBC reference <span className="tw-text-geist-error">*</span>
                        </label>
                        <UnderlineInput aria-label="HSBC reference" value={hsbcRef} onChange={(e) => setHsbcRef(e.target.value)}
                          placeholder="The reference HSBCnet shows for this payment" />
                      </div>
                    )}
                    <div>
                      <label className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-[#7a4a02]">
                        What HSBC says <span className="tw-text-geist-error">*</span>
                      </label>
                      <UnderlineInput value={resolveReason} onChange={(e) => setResolveReason(e.target.value)}
                        placeholder="e.g. checked HSBCnet, no transaction with this request number" />
                    </div>
                    <div className="tw-flex tw-gap-2">
                      <Button onClick={saveResolve} loading={busy}>{busy ? "Saving…" : "Save"}</Button>
                      <Clickable onClick={() => { setResolving(false); setHsbcRef(""); setResolveReason(""); setResolveOutcome("not_received"); }}
                        className="tw-h-9 tw-px-3 tw-text-[13px] tw-text-geist-fg-3">Cancel</Clickable>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Phiếu bị từ chối sau khi bút toán đã vào sổ ERP — Portal không có nghiệp vụ
            đảo bút toán, nên phải hiện ra để kế toán làm tay bên AX. */}
        {reversal.required && (
          <div className="tw-rounded-geist-sm tw-border tw-border-solid tw-border-[#f3c9c0] tw-bg-[#fdf2f0] tw-p-3.5">
            <div className="tw-flex tw-items-start tw-gap-2">
              <i className="bi bi-arrow-counterclockwise tw-text-[#8e2a18] tw-mt-0.5" />
              <div>
                <div className="tw-text-[13px] tw-font-bold tw-text-[#8e2a18]">
                  Reverse journal {reversal.journal_num} in AX
                </div>
                <p className="tw-text-[12px] tw-text-[#6e2313] tw-m-0 tw-mt-1">
                  The request was {reversal.cause === "canceled" ? "cancelled" : "rejected"} but the journal
                  is still posted in ERP{reversal.reason ? ` — reason: ${reversal.reason}` : ""}
                  {reversal.requested_by ? ` (${reversal.requested_by})` : ""}.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* What HSBC last said (status enquiry) — paid, still processing, or rejected with its reason. */}
        {bank.route === "hsbc" && (
          <HsbcStatusPanel bank={bank} canCheck={!!perms.allow_check_bank_status} busy={busy}
            onCheck={() => run(refreshPaymentRequestBankStatus(pr.id), "HSBC status checked.")} />
        )}

        {bank.route === 'hsbc' && pr.purchase_order_type === 'standard' && !byPaymentAccountant && <div role="status" className="tw-rounded-geist-sm tw-bg-geist-bg-2 tw-p-3 tw-text-[12px]">
          HSBC payment is sent automatically after the final BOD signature.
          {bank.automatic_release?.state && <span> Status: <b>{releaseStatus}</b>.</span>}
          {bank.automatic_release?.error && (
            <p className="tw-m-0 tw-mt-1">{releaseChecked ? "Last error: " : ""}{bank.automatic_release.error}</p>
          )}
          {bank.automatic_release?.attempts > 0 && (
            <p className="tw-m-0 tw-mt-1 tw-text-geist-fg-3">Sent again {bank.automatic_release.attempts}× · last by {bank.automatic_release.retried_by}</p>
          )}
          {/* Release after BOD failed: Accounting / admin sends the same signed payment again —
              only after an unknown outcome was checked on HSBCnet ("not received"). */}
          {perms.allow_retry_release && !retrying && (
            <Button size="sm" className="tw-mt-2" onClick={() => setRetrying(true)}>Send to HSBC again</Button>
          )}
          {perms.allow_retry_release && retrying && (
            <div className="tw-mt-2 tw-space-y-2">
              <UnderlineInput aria-label="Why send again" value={retryReason} onChange={(e) => setRetryReason(e.target.value)}
                placeholder="Why it is sent again, e.g. gateway fixed; HSBCnet shows no payment for this request" />
              <div className="tw-flex tw-gap-2">
                <Button size="sm" loading={busy} disabled={!retryReason.trim()} onClick={saveRetry}>{busy ? "Sending…" : "Send again"}</Button>
                <Clickable onClick={() => { setRetrying(false); setRetryReason(""); }} className="tw-h-8 tw-px-3 tw-text-[12px] tw-text-geist-fg-3">Cancel</Clickable>
              </div>
            </div>
          )}
          {!unknown && ["blocked", "rejected"].includes(bank.automatic_release?.state) && perms.bank_action_own_request && (
            <p className="tw-m-0 tw-mt-1 tw-text-[#7a4a02]">You created this request, so another accountant or admin must send it again.</p>
          )}
          {unknown && bank.automatic_release?.state === "unknown" && perms.allow_resolve_bank_uncertainty && (
            <p className="tw-m-0 tw-mt-1 tw-text-[#7a4a02]">Check HSBCnet first and record the result above — then you can send it again.</p>
          )}
        </div>}

        {/* Thao tác */}
        {perms.allow_change_bank_route && bank.route === "hsbc" && bank.status === "not_sent" && !unknown
          && (pr.purchase_order_type !== "standard" || disbursement.status === "processing") && (
          <div className="tw-flex tw-flex-wrap tw-gap-2 tw-pt-4 tw-border-0 tw-border-t tw-border-solid tw-border-geist-border">
            <Clickable disabled={busy} aria-busy={busy || undefined} onClick={busy ? undefined : () => run(submitPaymentRequestBankInstruction(pr.id), "Bank instruction sent.")}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-[13px] tw-font-semibold tw-text-geist-accent hover:tw-bg-geist-100">
              <i className={busy ? "bi bi-arrow-repeat tw-animate-spin" : "bi bi-send"} /> {busy ? "Sending…" : "Send instruction"}
            </Clickable>
          </div>
        )}
      </div>
    </div>
  );
};

IntegrationsCard.propTypes = {
  paymentRequest: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    purchase_order_type: PropTypes.string,
    // Bank state: route/status/reference, the HSBC outcome and the automatic release.
    bank: PropTypes.shape({
      live: PropTypes.bool,
      route: PropTypes.string,
      status: PropTypes.string,
      reference: PropTypes.string,
      outcome_unknown: PropTypes.bool,
      manual_reference: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
      resolved_by: PropTypes.string,
      resolved_reason: PropTypes.string,
      changed_by: PropTypes.string,
      changed_at: PropTypes.string,
      reason: PropTypes.string,
      last_error: PropTypes.string,
      automatic_release: PropTypes.shape({
        state: PropTypes.string,
        error: PropTypes.string,
        attempts: PropTypes.number,
        retried_by: PropTypes.string,
      }),
    }),
    // Keyed by operation name (create, post, ...) plus `live` and `sequence`.
    erp_operations: PropTypes.object,
    documents_state: PropTypes.shape({
      status: PropTypes.string,
      note: PropTypes.string,
      requested_by: PropTypes.string,
      requested_at: PropTypes.string,
    }),
    journal_reversal: PropTypes.shape({
      required: PropTypes.bool,
      journal_num: PropTypes.string,
      cause: PropTypes.string,
      reason: PropTypes.string,
      requested_by: PropTypes.string,
    }),
    disbursement: PropTypes.shape({
      status: PropTypes.string,
    }),
    permissions: PropTypes.shape({
      allow_reopen_bank_resolution: PropTypes.bool,
      allow_mark_disbursement_paid: PropTypes.bool,
      allow_resolve_bank_uncertainty: PropTypes.bool,
      allow_check_bank_status: PropTypes.bool,
      allow_retry_release: PropTypes.bool,
      allow_change_bank_route: PropTypes.bool,
      bank_action_own_request: PropTypes.bool,
    }),
  }),
};

export default IntegrationsCard;
